import {
  Component,
  Inject,
  NgZone,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
} from '@angular/core';
import { ApiHelper } from '../core/services/apiHelper';
import { environment } from '../environments/environment';
import { AuthService } from '../core/services/auth.service';
import { Subscription } from 'rxjs';
import { NavigationEnd, Router } from '@angular/router';
import { ErrorDialogService } from './shared/errorDialog/errorDialogService';
import { isPlatformBrowser } from '@angular/common';
import { shouldScrollToTop } from '../core/pure-functions/on-navigation-end-scroller-util/on-navigation-end-scroller-util';
import { WaterBodyNavigationService } from '../core/services/navigation/water-body-navigation.service';
import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';
import { ConstantMan } from '../core/services/constantMan';
import { ContainsSubscriptions } from '../core/services/interfaces/contains-subscriptions';
import { unsubscribeFromAll } from '../core/pure-functions/rx-helper';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, OnDestroy, ContainsSubscriptions {
  subs: Subscription[] = [];
  isErrorDialogActive = false;
  isBrowser: boolean;
  copyrightYear: string;
  date: string;
  isMobile = false;

  constructor(
    private apiHelper: ApiHelper,
    private authenticationService: AuthService,
    @Inject(PLATFORM_ID) private platformId: any,
    public router: Router,
    public errorDialogService: ErrorDialogService,
    private ngZone: NgZone,
    private waterBodyNavigationService: WaterBodyNavigationService,
    private breakpointObserver: BreakpointObserver,
  ) {
    const sub = this.breakpointObserver
      .observe(['(min-width: 960px)'])
      .subscribe((state: BreakpointState) => {
        this.isMobile = !state.matches;
      });
    this.isBrowser = isPlatformBrowser(this.platformId);
    let that = this;
    this.copyrightYear = new Date().getFullYear().toString();

    this.apiHelper.setEnvironment(environment.envName);
    const apiErrorSubscription = this.apiHelper.apiErrorChange$.subscribe(
      (apiError) => {
        this.ngZone.run(() => {
          console.log('API ERROR', apiError);
          if (!that.isErrorDialogActive) {
            that.isErrorDialogActive = true;
            this.errorDialogService
              .open(apiError.title, apiError.message)
              .subscribe((result) => {
                console.log(result);
                that.isErrorDialogActive = false;
              });
          }
        });
      },
    );

    this.subs.push(apiErrorSubscription);
  }

  ngOnInit() {
    if (this.isBrowser) {
      this.authenticationService.checkSessionPublic().subscribe(
        (data) => {},
        (error) => {
          this.router.navigate(['login']);
        },
      );
    }
    const routerSubscription = this.router.events.subscribe((event) => {
      let content = document.querySelector('.mat-sidenav-content') || window;
      if (event instanceof NavigationEnd) {
        setTimeout(function () {
          if (window['CallTrk']) {
            window['CallTrk'].swap();
          }
          if (shouldScrollToTop(content, event.urlAfterRedirects)) {
            content.scrollTo(0, 0);
          }
        }, 0);
      }
    });
    this.subs.push(routerSubscription);
  }

  ngOnDestroy() {
    unsubscribeFromAll(this.subs);
  }

  isOnNoNavList() {
    const { pathname } = window.location;
    return ConstantMan.NO_NAV_LIST.some((endpoint) =>
      pathname.includes(endpoint),
    );
  }

  isOnNoMobileNavList() {
    const { pathname } = window.location;
    return ConstantMan.NO_MOBILE_NAV_LIST.some((endpoint) =>
      pathname.includes(endpoint),
    );
  }

  isOnNoFooterList() {
    const { pathname } = window.location;
    return ConstantMan.NO_FOOTER_LIST.some((endpoint) =>
      pathname.includes(endpoint),
    );
  }

  onWaterbodyClick(vanityUrl, queryParams) {
    if (queryParams && sessionStorage.getItem('filterDate')) {
      this.date = sessionStorage.getItem('filterDate');
      this.waterBodyNavigationService.navWithQueryParams(vanityUrl, {
        ...queryParams,
        date: this.date,
      });
    } else {
      this.waterBodyNavigationService.navWithQueryParams(
        vanityUrl,
        queryParams,
      );
    }
  }

  footerSections = [
    {
      column: 1,
      title: 'Site Links',
      links: [
        { label: 'Home Page', routerLink: '/' },
        { label: 'Become an Anchor Captain', routerLink: '/become-a-captain' },
        { label: 'Become an Affiliate', routerLink: '/become-an-affiliate' },
        { label: 'List Your Boat', routerLink: '/list-my-boat' },
        { label: 'Boat Owner FAQ', routerLink: '/boat-owner-faq' },
        { label: 'FAQ', routerLink: '/passenger-faq' },
        { label: 'Contact', routerLink: '/contact-anchor' },
      ],
    },
    {
      column: 2,
      title: 'Anchor Locations',
      links: [
        {
          label: 'Miami',
          routerLink: '/rental-location-map/miami',
          onClick: () => this.onWaterbodyClick('miami', {}),
        },
        {
          label: 'Fort Lauderdale',
          routerLink: '/rental-location-map/fort-lauderdale',
          onClick: () => this.onWaterbodyClick('fort-lauderdale', {}),
        },
        {
          label: 'Naples',
          routerLink: '/rental-location-map/naples',
          onClick: () => this.onWaterbodyClick('naples', {}),
        },
        {
          label: 'St. Pete',
          routerLink: '/rental-location-map/st-pete',
          onClick: () => this.onWaterbodyClick('st-pete', {}),
        },
        {
          label: 'Tampa Bay',
          routerLink: '/rental-location-map/tampa-bay',
          onClick: () => this.onWaterbodyClick('tampa-bay', {}),
        },
        {
          label: 'Clearwater',
          routerLink: '/rental-location-map/clearwater',
          onClick: () => this.onWaterbodyClick('clearwater', {}),
        },
        {
          label: 'Sarasota',
          routerLink: '/rental-location-map/sarasota',
          onClick: () => this.onWaterbodyClick('sarasota', {}),
        },
        {
          label: 'Anna Maria Island',
          routerLink: '/rental-location-map/anna-maria-island',
          onClick: () => this.onWaterbodyClick('anna-maria-island', {}),
        },
        {
          label: 'Siesta Key',
          routerLink: '/rental-location-map/siesta-key',
          onClick: () => this.onWaterbodyClick('siesta-key', {}),
        },
        {
          label: 'Lake of the Ozarks',
          routerLink: '/rental-location-map/lake-of-the-ozarks',
          onClick: () => this.onWaterbodyClick('lake-of-the-ozarks', {}),
        },
        {
          label: 'West Palm Beach',
          routerLink: '/rental-location-map/west-palm-beach',
          onClick: () => this.onWaterbodyClick('west-palm-beach', {}),
        },
        {
          label: 'Florida Keys',
          routerLink: '/rental-location-map/florida-keys',
          onClick: () => this.onWaterbodyClick('florida-keys', {}),
        },
      ],
    },
    {
      column: 3,
      title: 'Legal',
      links: [
        { label: 'Terms of Use', routerLink: '/terms-of-use' },
        { label: 'Privacy Policy', routerLink: '/privacy-policy' },
        {
          label: 'Certificate of Insurance',
          href: 'https://anchor-public-media.s3.us-east-2.amazonaws.com/prod/Anchor_COI.pdf',
        },
        {
          label: 'Boat Owner Agreement',
          href: 'https://anchor-public-media.s3.us-east-2.amazonaws.com/prod/Boat_Owner_Agreement.pdf',
        },
        {
          label: 'Release Waiver',
          href: 'https://anchor-public-media.s3.us-east-2.amazonaws.com/prod/Release_Waiver_and_Indemnification.pdf',
        },
        {
          label: 'Day Charter Cancellation Policy',
          href: 'https://anchor-public-media.s3.us-east-2.amazonaws.com/prod/Day_Charter_Cancellation_Policy_08_10_2024.pdf',
        },
        {
          label: 'Term Charter Cancellation Policy',
          href: 'https://anchor-public-media.s3.us-east-2.amazonaws.com/prod/Term_Charter_Cancellation_Policy_08_10_2024.pdf',
        },
      ],
    },
  ];
}
